import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { authAPI, patchCurrentUser } from '../services/api';

const THEME_KEY = 'vantagepoint-theme';
const PREFERRED_THEME = 'dark';

if (typeof document !== 'undefined') {
  document.documentElement.classList.add('dark');
}

function applyDarkTheme() {
  const root = typeof document !== 'undefined' ? document.documentElement : null;
  if (root) {
    root.classList.add('dark');
  }
}

type ThemeContextValue = {
  preferredTheme: string;
  setPreferredTheme: () => void;
  effectiveDark: boolean;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: any) {
  useEffect(() => {
    applyDarkTheme();
    if (typeof window !== 'undefined') {
      localStorage.setItem(THEME_KEY, PREFERRED_THEME);
    }
  }, []);

  // Normalise legacy light/system preferences stored on the user record.
  useEffect(() => {
    if (typeof window === 'undefined' || !localStorage.getItem('token')) return;
    let cancelled = false;
    authAPI
      .getSettings()
      .then((res) => {
        if (cancelled) return;
        const s = res.settings;
        const t = s?.preferredTheme;
        if (t && t !== PREFERRED_THEME) {
          authAPI.updateSettings({ preferredTheme: PREFERRED_THEME }).catch(() => undefined);
        }
        if (s && (s.githubId !== undefined || s.githubUsername !== undefined)) {
          patchCurrentUser({
            githubId: s.githubId ?? null,
            githubUsername: s.githubUsername ?? null,
          });
        }
      })
      .catch(() => undefined); // optional settings sync on mount

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      preferredTheme: PREFERRED_THEME,
      setPreferredTheme: () => {},
      effectiveDark: true,
    }),
    []
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
